/**
 * vapor-chamber-router — composables. Vue 3.6-native: cleanup via
 * onScopeDispose directly, reads through the router's shallowRefs.
 */

import { computed, getCurrentScope, inject, onScopeDispose } from 'vue';
import { ROUTER_KEY } from './keys';
import { type Breadcrumb, type MenuItem, buildBreadcrumbs, buildMenu } from './menu';
import type { Router } from './router-type';
import type { QueryParamDef, RouteLocation } from './types';
import { decodeQueryParam, encodeQueryParam, resolveQueryHistory } from './url';

export function useRouter(): Router {
  const router = inject<Router>(ROUTER_KEY);
  if (!router) throw new Error('[vapor-chamber-router] no router provided — did you app.use(router)?');
  return router;
}

/** Reactive current location (URL state — outlets read snapshot.render). */
export function useRoute(): { readonly value: RouteLocation } {
  const router = useRouter();
  return computed(() => router.currentRoute.value.location);
}

export type QueryParamHandle<T> = {
  /** Reactive read; writes use the resolved default history mode
   *  (route declaration → convention: 'page' pushes, the rest replaces). */
  value: T;
  /** Write with an explicit pushState (back returns to the old value). */
  push: (next: T) => void;
  /** Write with an explicit replaceState (no history entry). */
  replace: (next: T) => void;
  /** Remove the key from the URL (reads fall back to the declared default). */
  clear: () => void;
};

/**
 * Typed, writable access to one query param through the query fast path —
 * never triggers matching, guards or remounts. Loaders whose template
 * depends on the key refetch automatically.
 */
export function useQueryParam<T = unknown>(key: string, def?: QueryParamDef): QueryParamHandle<T> {
  const router = useRouter();
  const definition = (): QueryParamDef => {
    if (def) return def;
    const matched = router.currentRoute.value.location.matched;
    return matched[matched.length - 1]?.queryDefs[key] ?? {};
  };
  const write = (next: T | null, override?: 'push' | 'replace') => {
    const d = definition();
    router.setQuery(
      { [key]: next === null ? null : encodeQueryParam(next, d) },
      { history: resolveQueryHistory(key, d, override) },
    );
  };
  return {
    get value(): T {
      return decodeQueryParam(router.currentRoute.value.location.query[key], definition()) as T;
    },
    set value(next: T) {
      write(next);
    },
    push: (next) => write(next, 'push'),
    replace: (next) => write(next, 'replace'),
    clear: () => write(null, 'replace'),
  };
}

/**
 * Loader data of the current route (two-phase committed — never the previous
 * page's data). By default the LEAF record's data; pass a record name when a
 * layout ancestor also loads.
 */
export function useRouteData<T = unknown>(recordName?: string): { readonly value: T | undefined } {
  const router = useRouter();
  return computed(() => {
    const { location, data } = router.currentRoute.value;
    const name = recordName ?? location.matched[location.matched.length - 1]?.name;
    return (name ? data.get(name) : undefined) as T | undefined;
  });
}

/**
 * Route-level error boundary: the latest navigation error (RouterError with
 * a machine-readable `code`) plus clear().
 */
export function useRouteError(): { latestError: { readonly value: unknown }; clear: () => void } {
  const router = useRouter();
  return {
    latestError: router.lastError,
    clear: () => {
      router.lastError.value = null;
    },
  };
}

/**
 * The navigation menu, projected from the route table (see menu.ts for the
 * contract: `meta.menu` position + `meta.title` i18n key, nested by nearest
 * menued ancestor, server-side permission filtering, data-active semantics).
 * Reactive to navigation AND table swaps (setRoutes / reload).
 */
export function useMenu(): { readonly value: readonly MenuItem[] } {
  const router = useRouter();
  return computed(() => buildMenu(router.routes.value, router.currentRoute.value.location.path, router.base));
}

/**
 * Parent-chain breadcrumbs of the current route: matched records carrying a
 * `meta.title`, root-first, the page itself last (`current: true`). Group
 * rows and ancestors whose params the location can't supply come href-less.
 */
export function useBreadcrumbs(): { readonly value: readonly Breadcrumb[] } {
  const router = useRouter();
  return computed(() => buildBreadcrumbs(router.currentRoute.value.location, router.base));
}

/**
 * Leave guard scoped to the calling component's lifetime: fires on every
 * PATH navigation (query changes never trigger it) while mounted; return
 * false to refuse. Pairs with dirty-state trackers for unsaved-changes
 * protection. Auto-disposes with the component scope.
 */
export function onBeforeLeave(
  guard: (to: RouteLocation, from: RouteLocation) => boolean | void | Promise<boolean | void>,
): () => void {
  const router = useRouter();
  const off = router.beforeEach(async (to, from) => {
    const verdict = await guard(to, from);
    return verdict === false ? false : undefined;
  });
  if (getCurrentScope()) onScopeDispose(off);
  return off;
}
